import { db } from "../db/index.js";
import {
  getFavoritesToPush,
  markFavoritePushed,
  ensureCloudTabMeta,
} from "../db/operations.js";
import { apiClient, ApiError, FavoritesResponse } from "../utils/apiClient.js";
import { useAuthStore } from "../stores/authStore.js";
import { LIBRARY_CHANGED_EVENT } from "./events.js";
import { applyServerDifficulty } from "../db/difficulty.js";

/**
 * Favorites sync (FRE-143, replaces the FRE-141 localStorage toggle queue).
 *
 * Favorites are desired state: Dexie `userFavorites` rows carry `dirty`, `serverKnown` and a `removedAt`
 * tombstone. Push sends idempotent PUT / DELETE /api/tabs/:id/favorite, then pull reads
 * GET /api/tabs/favorites. Dirty rows wait for their owner to sign in; nothing is purged on logout.
 */

let inFlight: Promise<void> | null = null;
let rerunRequested = false;

const statusOf = (err: unknown): number | undefined =>
  err instanceof ApiError ? err.status : undefined;

async function pushFavorites(uid: string, token: string): Promise<void> {
  const rows = await getFavoritesToPush(uid);
  for (const row of rows) {
    // Account guard: never send under another account's token.
    const auth = useAuthStore.getState();
    if (auth.token !== token || auth.user?.id !== uid) return;

    const tab = await db.tabs.get(row.tabId);
    if (tab?.pendingUpload === 1) continue; // server does not know the tab yet; re-dirtied after its upload

    const snapshot = row.updatedAt;
    // FND-32: a tab whose own DELETE /api/tabs/:id is queued takes its favorite rows with it on the server
    // (cascade), so a favorite DELETE would be redundant (and 404 once the tab delete lands).
    if (row.removedAt && tab?.removedAt && tab.pendingServerDelete === 1) {
      await markFavoritePushed(uid, row.tabId, snapshot, "removed");
      continue;
    }
    try {
      if (row.removedAt) {
        await apiClient.delete(`/api/tabs/${row.tabId}/favorite`);
        await markFavoritePushed(uid, row.tabId, snapshot, "removed");
      } else {
        await apiClient.put(`/api/tabs/${row.tabId}/favorite`, {});
        await markFavoritePushed(uid, row.tabId, snapshot, "on");
      }
    } catch (err) {
      const status = statusOf(err);
      if (status === 401) return; // apiClient already logs out
      if (!row.removedAt && (status === 404 || status === 400)) {
        // Tab not viewable on the server: stays a local-only favorite, no retry loop.
        await markFavoritePushed(uid, row.tabId, snapshot, "local");
        continue;
      }
      if (row.removedAt && status === 404) {
        await markFavoritePushed(uid, row.tabId, snapshot, "removed");
        continue;
      }
      throw err; // network / 5xx: stop the run, skip the pull
    }
  }
}

async function pullFavorites(uid: string): Promise<void> {
  // Removal candidates are snapshotted before the request so a local change that lands in between is never
  // mistaken for a remote removal.
  const before = await db.userFavorites.where("ownerId").equals(uid).toArray();
  const candidates = new Map<string, string>();
  for (const f of before) {
    if (f.dirty === 0 && f.serverKnown === 1 && !f.removedAt) candidates.set(f.tabId, f.updatedAt);
  }

  const res = await apiClient.get<FavoritesResponse>("/api/tabs/favorites");
  // Never treat an error or an odd shape (e.g. a catch-all mock) as an empty list.
  if (!Array.isArray(res?.data)) return;
  const items = res.data.filter((i) => i && typeof i.tabId === "string" && i.tab);
  const serverIds = new Set(items.map((i) => i.tabId));

  let changed = false;
  await db.transaction("rw", [db.userFavorites, db.tabs, db.tabEdits], async () => {
    for (const item of items) {
      if (await ensureCloudTabMeta(item.tab, uid)) changed = true;
      // FRE-188 TL-10: difficulty is pulled unless an owner edit is still queued
      if (await applyServerDifficulty(item.tab.id, item.tab)) changed = true;
      const local = await db.userFavorites.get([uid, item.tabId]);
      if (!local) {
        await db.userFavorites.put({
          ownerId: uid,
          tabId: item.tabId,
          addedAt: item.createdAt,
          updatedAt: item.createdAt,
          dirty: 0,
          serverKnown: 1,
        });
        changed = true;
        continue;
      }
      if (local.dirty === 1) continue; // local wins
      if (local.serverKnown !== 1) await db.userFavorites.put({ ...local, serverKnown: 1 });
    }
    for (const [tabId, snapshot] of candidates) {
      if (serverIds.has(tabId)) continue;
      const cur = await db.userFavorites.get([uid, tabId]);
      if (cur && cur.dirty === 0 && !cur.removedAt && cur.updatedAt === snapshot) {
        await db.userFavorites.delete([uid, tabId]); // unfavorited on another device
        changed = true;
      }
    }
  });

  if (changed) window.dispatchEvent(new Event(LIBRARY_CHANGED_EVENT));
}

async function runOnce(): Promise<void> {
  const { token, isGuest, user } = useAuthStore.getState();
  if (!token || isGuest || !user) return;
  await pushFavorites(user.id, token);
  await pullFavorites(user.id);
}

/** Push then pull. Single in-flight run; a call during a run schedules exactly one more. Guests: no-op. */
export function syncFavorites(): Promise<void> {
  if (inFlight) {
    rerunRequested = true;
    return inFlight;
  }
  inFlight = (async () => {
    try {
      do {
        rerunRequested = false;
        await runOnce();
      } while (rerunRequested);
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/** Fire and forget; no-op offline; errors are swallowed. */
export function requestFavoriteSync(): void {
  if (typeof navigator !== "undefined" && !navigator.onLine) return;
  syncFavorites().catch((err) => {
    console.warn("favoriteSync: background sync failed", err);
  });
}
